import { useEffect, useState } from 'react'
import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { EyeOff, MoreHorizontal, Pencil, Save, Send, X } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { isSystemOrgAdminRole } from '@/lib/route-access'
import { useAuthStore } from '@/stores/auth-store'
import {
  orgTreeApi,
  type OrgTreeCredentialSettings,
  type OrgTreeCredentialTestPayload,
  type OrgTreeCredentialValue,
} from '@/services/org-tree'

type CredentialFieldConfig = {
  field: string
  label: string
  secret?: boolean
  boolean?: boolean
  multiline?: boolean
}

type CredentialSectionConfig = {
  key: string
  label: string
  description: string
  fields: CredentialFieldConfig[]
}

const credentialSections: CredentialSectionConfig[] = [
  {
    key: 'fcm',
    label: 'Firebase Cloud Messaging',
    description: 'Paste the Firebase service account JSON key downloaded from your project. Credentials are stored encrypted per tenant.',
    fields: [
      { field: 'project_id', label: 'Project ID' },
      { field: 'service_account_json', label: 'Service account JSON', secret: true, multiline: true },
    ],
  },
  {
    key: 'google',
    label: 'Google Maps',
    description: 'Maps API credentials used for geocoding and fare calculations.',
    fields: [
      { field: 'maps_api_key', label: 'Maps API key', secret: true },
    ],
  },
  {
    key: 'payment',
    label: 'Payment Gateway',
    description: 'Gateway endpoints for payment login and retrieval flows.',
    fields: [
      { field: 'webxpay_login_api_url', label: 'WebXPay login URL' },
      { field: 'webxpay_retrieval_api_url', label: 'WebXPay retrieval URL' },
    ],
  },
  {
    key: 'storage',
    label: 'Storage',
    description: 'S3 bucket and credentials when using S3. Storage type (Local vs S3) is set under the Configuration tab.',
    fields: [
      { field: 'bucket_name', label: 'Bucket name' },
      { field: 'region_name', label: 'Region name' },
      { field: 'access_key', label: 'Access key', secret: true },
      { field: 'secret_key', label: 'Secret key', secret: true },
    ],
  },
]

const fieldToPayloadKey: Record<string, string> = {
  account_sid: 'twilio_account_sid',
  auth_token: 'twilio_auth_token',
  phone_number: 'twilio_phone_number',
  service_id: 'twilio_service_id',
  host: 'email_host',
  port: 'email_port',
  host_user: 'email_host_user',
  host_password: 'email_host_password',
  plain_text_mode: 'order_email_plain_text',
  gateway_type: 'sms_gateway_type',
  sender: 'mobile_otp_sender',
  api_url: 'mobile_otp_api_url',
  api_key: 'mobile_otp_api_key',
  url: 'fcm_url',
  project_id: 'fcm_project_id',
  server_key: 'fcm_server_key',
  service_account_json: 'google_application_credentials_json',
  service_account_path: 'google_application_credentials_path',
  maps_api_key: 'google_map_api_key',
  webxpay_login_api_url: 'webxpay_login_api_url',
  webxpay_retrieval_api_url: 'webxpay_retrieval_api_url',
  bucket_name: 'storage_bucket_name',
  region_name: 'storage_region_name',
  access_key: 'storage_access_key',
  secret_key: 'storage_secret_key',
  api_version: 'whatsapp_api_version',
  phone_number_id: 'whatsapp_phone_number_id',
  access_token: 'whatsapp_access_token',
  chat_bot_token: 'chat_bot_token',
}

type Props = {
  serviceInstanceId: string | number
  title?: string
  description?: string
}

type FormState = Record<string, string>
type TestChannel = 'push' | 'storage' | 'google_maps'
type TestFormState = Record<TestChannel, { destination: string; subjectOrTitle: string; body: string }>

const initialTestFormState: TestFormState = {
  push: {
    destination: '',
    subjectOrTitle: 'Credential Test Push',
    body: 'This is a test push notification from the credential manager.',
  },
  storage: {
    destination: '',
    subjectOrTitle: '',
    body: 'storage validation',
  },
  google_maps: {
    destination: 'Colombo',
    subjectOrTitle: '',
    body: 'map validation',
  },
}

const testChannelMeta: Array<{
  channel: TestChannel
  label: string
  destinationLabel: string
  helperText: string
}> = [
  {
    channel: 'push',
    label: 'Push Test',
    destinationLabel: 'Destination device ID',
    helperText: 'Sends a direct push notification to the provided device token.',
  },
  {
    channel: 'storage',
    label: 'Storage Test',
    destinationLabel: 'Probe object key (optional)',
    helperText: 'Validates active storage configuration. For S3, optionally checks a specific object key.',
  },
  {
    channel: 'google_maps',
    label: 'Google Maps Test',
    destinationLabel: 'Search query',
    helperText: 'Runs a geocode request using configured Google Maps credentials.',
  },
]

const sectionTestChannelMap: Partial<Record<string, TestChannel>> = {
  fcm: 'push',
  google: 'google_maps',
  storage: 'storage',
}

const getEntry = (settings: OrgTreeCredentialSettings | null | undefined, sectionKey: string, fieldKey: string) => {
  const section = settings?.sections?.[sectionKey] as Record<string, unknown> | undefined
  const entry = section?.[fieldKey]
  if (!entry || typeof entry !== 'object') {
    return null
  }
  return entry as OrgTreeCredentialValue
}

const sectionKnownKeyLabels: Record<string, Record<string, string>> = {
  storage: {
    type: 'Storage type (set under Configuration)',
  },
}

const getMissingRequiredFieldLabels = (section: CredentialSectionConfig, sectionData: Record<string, unknown> | undefined) => {
  const missingFields = Array.isArray(sectionData?.missing_required_fields)
    ? sectionData.missing_required_fields
    : []

  return missingFields.map((fieldKey) => {
    const field = section.fields.find((entry) => entry.field === fieldKey || fieldToPayloadKey[entry.field] === fieldKey || entry.field === fieldKey)
    if (field) {
      return field.label
    }

    const matchedByUiKey = section.fields.find((entry) => {
      const sectionFieldMap: Record<string, string> = {
        [entry.field]: entry.label,
      }
      return sectionFieldMap[fieldKey] != null
    })

    const knownLabel = sectionKnownKeyLabels[section.key]?.[fieldKey]

    return matchedByUiKey?.label || knownLabel || String(fieldKey).split('_').join(' ')
  })
}

export default function ServiceInstanceCredentialManager({ serviceInstanceId, title = 'Credential Manager', description = 'View and update tenant-specific credentials with service-branch override and organisation fallback.' }: Props) {
  const queryClient = useQueryClient()
  const currentPost = useAuthStore((state) => state.currentPost)
  const currentCapabilities = currentPost?.capabilities
  const [form, setForm] = useState<FormState>({})
  const [clearedSecrets, setClearedSecrets] = useState<Record<string, boolean>>({})
  const [editingSections, setEditingSections] = useState<Record<string, boolean>>({})
  const [testForm, setTestForm] = useState<TestFormState>(initialTestFormState)
  const [activeTestChannel, setActiveTestChannel] = useState<TestChannel | null>(null)
  const [expandedTestSection, setExpandedTestSection] = useState<string | null>(null)
  const [activeCredentialTab, setActiveCredentialTab] = useState<string>(credentialSections[0]?.key ?? 'fcm')

  const hasCapabilityContext = currentCapabilities != null
  // Align with sidebar/feature key `can_manage_credentials` and backend aliases.
  // Prefer OR over ?? so an explicit false on one key does not block sibling grants.
  const canUpdateCredentialSettings = !hasCapabilityContext
    || isSystemOrgAdminRole(currentPost)
    || Boolean(
      currentCapabilities?.can_update_service_instance_credentials
      || currentCapabilities?.can_manage_credentials
      || currentCapabilities?.can_manage_org_settings
      || currentCapabilities?.can_view_service_instance_credentials
      || currentCapabilities?.can_view_service_instance_details,
    )
  const canTestCredentialSettings = !hasCapabilityContext
    || isSystemOrgAdminRole(currentPost)
    || Boolean(
      currentCapabilities?.can_test_service_instance_credentials
      || currentCapabilities?.can_update_service_instance_credentials
      || currentCapabilities?.can_manage_credentials
      || currentCapabilities?.can_manage_org_settings
      || currentCapabilities?.can_view_service_instance_credentials
      || currentCapabilities?.can_view_service_instance_details,
    )

  const credentialQuery = useQuery({
    queryKey: ['org-tree-service-instance-credentials', serviceInstanceId],
    queryFn: () => orgTreeApi.getServiceInstanceCredentials(serviceInstanceId),
    enabled: Boolean(serviceInstanceId),
  })

  // With the server's common S3 the org's own storage settings do nothing, so they are hidden.
  const storageManagedByPlatform = Boolean(credentialQuery.data?.storage_managed_by_platform)
  const visibleCredentialSections = storageManagedByPlatform
    ? credentialSections.filter((section) => section.key !== 'storage')
    : credentialSections

  const isCredentialForbidden =
    axios.isAxiosError(credentialQuery.error) &&
    (credentialQuery.error.response?.status === 401 || credentialQuery.error.response?.status === 403)

  useEffect(() => {
    if (!credentialQuery.data) {
      return
    }

    const nextForm: FormState = {}
    credentialSections.forEach((section) => {
      section.fields.forEach((field) => {
        const entry = getEntry(credentialQuery.data, section.key, field.field)
        if (field.secret) {
          nextForm[fieldToPayloadKey[field.field]] = ''
          return
        }
        const rawValue = entry?.value
        if (typeof rawValue === 'boolean') {
          nextForm[fieldToPayloadKey[field.field]] = rawValue ? 'true' : 'false'
          return
        }
        nextForm[fieldToPayloadKey[field.field]] = rawValue == null ? '' : String(rawValue)
      })
    })

    setForm(nextForm)
    setClearedSecrets({})
    setEditingSections({})
    setExpandedTestSection(null)
  }, [credentialQuery.data])

  const resetSection = (section: CredentialSectionConfig) => {
    if (!credentialQuery.data) {
      return
    }

    setForm((current) => {
      const nextForm = { ...current }
      section.fields.forEach((field) => {
        const payloadKey = fieldToPayloadKey[field.field]
        const entry = getEntry(credentialQuery.data, section.key, field.field)
        if (field.secret) {
          nextForm[payloadKey] = ''
          return
        }

        const rawValue = entry?.value
        if (typeof rawValue === 'boolean') {
          nextForm[payloadKey] = rawValue ? 'true' : 'false'
          return
        }

        nextForm[payloadKey] = rawValue == null ? '' : String(rawValue)
      })
      return nextForm
    })

    setClearedSecrets((current) => {
      const nextState = { ...current }
      section.fields.forEach((field) => {
        delete nextState[fieldToPayloadKey[field.field]]
      })
      return nextState
    })
  }

  const updateMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {}

      visibleCredentialSections.forEach((section) => {
        section.fields.forEach((field) => {
          const payloadKey = fieldToPayloadKey[field.field]
          const currentValue = form[payloadKey] ?? ''

          if (field.secret) {
            if (clearedSecrets[payloadKey]) {
              payload[payloadKey] = ''
            } else if (currentValue.trim()) {
              payload[payloadKey] = currentValue.trim()
            }
            return
          }

          if (field.boolean) {
            payload[payloadKey] = currentValue === 'true'
            return
          }

          payload[payloadKey] = currentValue.trim()
        })
      })

      return orgTreeApi.updateServiceInstanceCredentials(serviceInstanceId, payload)
    },
    onSuccess: async () => {
      toast.success('Credential manager updated successfully')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['org-tree-service-instance-credentials', serviceInstanceId] }),
        queryClient.invalidateQueries({ queryKey: ['org-tree-service-instance', serviceInstanceId] }),
      ])
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const testMutation = useMutation({
    mutationFn: async ({ payload }: { channel: TestChannel; payload: OrgTreeCredentialTestPayload }) => {
      return orgTreeApi.testServiceInstanceCredentials(serviceInstanceId, payload)
    },
    onSuccess: (response, variables) => {
      const channelLabel = testChannelMeta.find((item) => item.channel === variables.channel)?.label || variables.channel
      const message = typeof response === 'object' && response && 'message' in response ? String((response as Record<string, unknown>).message ?? '') : ''
      toast.success(message || `${channelLabel} sent successfully`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
    onSettled: () => {
      setActiveTestChannel(null)
    },
  })

  const runTest = (channel: TestChannel) => {
    const current = testForm[channel]
    const destination = current.destination.trim()
    const body = current.body.trim()

    const requiresDestination = channel !== 'storage'
    const requiresBody = channel !== 'storage' && channel !== 'google_maps'

    if ((requiresDestination && !destination) || (requiresBody && !body)) {
      if (requiresDestination && requiresBody) {
        toast.error('Destination and body are required.')
      } else if (requiresDestination) {
        toast.error('Destination is required.')
      } else {
        toast.error('Body is required.')
      }
      return
    }

    const payload: OrgTreeCredentialTestPayload = {
      channel,
      body,
    }

    if (channel === 'push') {
      payload.destination_device_id = destination
      payload.title = current.subjectOrTitle.trim() || 'Credential Test Push'
    }

    if (channel === 'storage' || channel === 'google_maps') {
      payload.destination = destination
    }

    setActiveTestChannel(channel)
    testMutation.mutate({ channel, payload })
  }

  if (credentialQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full rounded-[24px]" />
        <Skeleton className="h-40 w-full rounded-[24px]" />
      </div>
    )
  }

  if (credentialQuery.error) {
    if (isCredentialForbidden) {
      return (
        <Card className="rounded-[24px] border-destructive/20">
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            <CardDescription>You do not have permission to view credential settings for this Service Branch.</CardDescription>
          </CardHeader>
        </Card>
      )
    }

    return (
      <Card className="rounded-[24px] border-destructive/20">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>Failed to load credential settings.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-destructive">{getErrorMessage(credentialQuery.error)}</CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="rounded-[24px] border-border/80">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>
            Effective source: <span className="font-medium text-foreground">{credentialQuery.data?.source || 'none'}</span>. Use Edit on a card to unlock its fields and Test on supported cards to send a live verification.
            {!canUpdateCredentialSettings ? ' Your role can view these settings but cannot edit them.' : ''}
            {canUpdateCredentialSettings && !canTestCredentialSettings ? ' Your role can edit these settings but cannot run live credential tests.' : ''}
            {storageManagedByPlatform ? ' File storage is provided by the platform for every organization, so there are no storage settings here.' : ''}
          </p>
          <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending || !canUpdateCredentialSettings}>
            <Save className="mr-2 h-4 w-4" />
            Save Credentials
          </Button>
        </CardContent>
      </Card>

      <Tabs value={activeCredentialTab} onValueChange={setActiveCredentialTab} className="space-y-4">
        <div className="overflow-x-auto rounded-[24px] border border-border/80 bg-card p-3">
          <TabsList className="inline-flex h-auto min-w-max gap-2 bg-transparent p-0">
            {visibleCredentialSections.map((section) => (
              <TabsTrigger key={section.key} value={section.key}>{section.label}</TabsTrigger>
            ))}
          </TabsList>
        </div>

        {visibleCredentialSections.map((section) => {
          const sectionData = credentialQuery.data?.sections?.[section.key]
          const missingRequiredLabels = getMissingRequiredFieldLabels(section, sectionData as Record<string, unknown> | undefined)
          const isEditing = Boolean(editingSections[section.key])
          const sectionTestChannel = sectionTestChannelMap[section.key]
          const testMeta = sectionTestChannel ? testChannelMeta.find((item) => item.channel === sectionTestChannel) ?? null : null
          const testState = testMeta ? testForm[testMeta.channel] : null
          const isTestExpanded = expandedTestSection === section.key && testMeta != null

          return (
            <TabsContent key={section.key} value={section.key} className="mt-0">
              <Card className="rounded-[24px] border-border/80">
                <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-1.5">
                    <CardTitle>{section.label}</CardTitle>
                    <CardDescription>{section.description}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {testMeta ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!canTestCredentialSettings}
                        onClick={() => setExpandedTestSection((current) => current === section.key ? null : section.key)}
                      >
                        <Send className="mr-2 h-4 w-4" />
                        {isTestExpanded ? 'Hide Test' : 'Test'}
                      </Button>
                    ) : null}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Section actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isEditing ? (
                          <DropdownMenuItem
                            disabled={!canUpdateCredentialSettings}
                            onClick={() => {
                              resetSection(section)
                              setEditingSections((current) => ({ ...current, [section.key]: false }))
                            }}
                          >
                            <X className="mr-2 h-4 w-4" />
                            Cancel editing
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            disabled={!canUpdateCredentialSettings}
                            onClick={() => setEditingSections((current) => ({ ...current, [section.key]: true }))}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!sectionData?.configured && missingRequiredLabels.length > 0 ? (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                      Missing required fields: {missingRequiredLabels.join(', ')}
                    </div>
                  ) : null}
                  {!isEditing ? (
                    <div className="rounded-2xl border border-dashed border-border/70 bg-muted/10 px-4 py-3 text-sm text-muted-foreground">
                      {canUpdateCredentialSettings
                        ? 'This card is read-only until you choose Edit.'
                        : 'Your role can view this card but cannot edit these settings.'}
                    </div>
                  ) : null}
                  <div className="grid gap-4 md:grid-cols-2">
                    {section.fields.map((field) => {
                      const payloadKey = fieldToPayloadKey[field.field]
                      const entry = getEntry(credentialQuery.data, section.key, field.field)
                      const helperText = field.secret
                        ? clearedSecrets[payloadKey]
                          ? 'This secret will be cleared on save. Organisation fallback will be used if available.'
                          : entry?.configured
                            ? `Saved value: ${entry.masked_value || 'configured'} (${entry.source})`
                            : 'No saved value'
                        : entry?.configured
                          ? `Effective value comes from ${entry.source}.`
                          : 'No saved value'

                      return (
                        <div key={payloadKey} className={`space-y-2${field.multiline ? ' md:col-span-2' : ''}`}>
                          <div className="flex items-center justify-between gap-2">
                            <Label htmlFor={payloadKey}>{field.label}</Label>
                            {field.secret ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-auto px-2 py-1 text-xs"
                                disabled={!isEditing}
                                onClick={() => setClearedSecrets((current) => ({ ...current, [payloadKey]: !current[payloadKey] }))}
                              >
                                <EyeOff className="mr-1 h-3.5 w-3.5" />
                                {clearedSecrets[payloadKey] ? 'Keep' : 'Clear'}
                              </Button>
                            ) : null}
                          </div>

                          {field.boolean ? (
                            <Select value={form[payloadKey] ?? 'false'} onValueChange={(value) => setForm((current) => ({ ...current, [payloadKey]: value }))} disabled={!isEditing}>
                              <SelectTrigger id={payloadKey}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="false">HTML</SelectItem>
                                <SelectItem value="true">Plain text</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : field.multiline ? (
                            <Textarea
                              id={payloadKey}
                              value={form[payloadKey] ?? ''}
                              placeholder="Paste the full service account JSON downloaded from Firebase"
                              disabled={!isEditing}
                              rows={8}
                              className="font-mono text-xs"
                              onChange={(event) => {
                                const value = event.target.value
                                setForm((current) => ({ ...current, [payloadKey]: value }))
                                if (field.secret && clearedSecrets[payloadKey]) {
                                  setClearedSecrets((current) => ({ ...current, [payloadKey]: false }))
                                }
                              }}
                            />
                          ) : (
                            <Input
                              id={payloadKey}
                              type={field.secret ? 'password' : 'text'}
                              value={form[payloadKey] ?? ''}
                              placeholder={field.secret ? 'Enter a new value to replace the saved secret' : ''}
                              disabled={!isEditing}
                              onChange={(event) => {
                                const value = event.target.value
                                setForm((current) => ({ ...current, [payloadKey]: value }))
                                if (field.secret && clearedSecrets[payloadKey]) {
                                  setClearedSecrets((current) => ({ ...current, [payloadKey]: false }))
                                }
                              }}
                            />
                          )}

                          <p className="text-xs text-muted-foreground">{helperText}</p>
                          {sectionData && typeof sectionData.source === 'string' ? (
                            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground/80">Section source: {sectionData.source}</p>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>

                  {testMeta && testState && isTestExpanded ? (
                    <div className="space-y-3 rounded-[20px] border border-border/70 bg-muted/15 p-4">
                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold text-foreground">{testMeta.label}</h3>
                        <p className="text-xs text-muted-foreground">{testMeta.helperText}</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`${section.key}-destination`}>{testMeta.destinationLabel}</Label>
                        <Input
                          id={`${section.key}-destination`}
                          value={testState.destination}
                          onChange={(event) => {
                            const value = event.target.value
                            setTestForm((current) => ({
                              ...current,
                              [testMeta.channel]: { ...current[testMeta.channel], destination: value },
                            }))
                          }}
                        />
                      </div>

                      {testMeta.channel === 'push' ? (
                        <div className="space-y-2">
                          <Label htmlFor={`${section.key}-subject`}>Title</Label>
                          <Input
                            id={`${section.key}-subject`}
                            value={testState.subjectOrTitle}
                            onChange={(event) => {
                              const value = event.target.value
                              setTestForm((current) => ({
                                ...current,
                                [testMeta.channel]: { ...current[testMeta.channel], subjectOrTitle: value },
                              }))
                            }}
                          />
                        </div>
                      ) : null}

                      {testMeta.channel !== 'storage' && testMeta.channel !== 'google_maps' ? (
                        <div className="space-y-2">
                          <Label htmlFor={`${section.key}-body`}>Body</Label>
                          <Textarea
                            id={`${section.key}-body`}
                            rows={4}
                            value={testState.body}
                            onChange={(event) => {
                              const value = event.target.value
                              setTestForm((current) => ({
                                ...current,
                                [testMeta.channel]: { ...current[testMeta.channel], body: value },
                              }))
                            }}
                          />
                        </div>
                      ) : null}

                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          disabled={!canTestCredentialSettings || (testMutation.isPending && activeTestChannel === testMeta.channel)}
                          onClick={() => runTest(testMeta.channel)}
                        >
                          <Send className="mr-2 h-4 w-4" />
                          {testMutation.isPending && activeTestChannel === testMeta.channel ? 'Sending…' : `Send ${testMeta.label}`}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </TabsContent>
          )
        })}
      </Tabs>
    </div>
  )
}